import React, { useState, useEffect } from 'react';
import axios from 'axios';

function DiaryTemplateManager({ onClose }) {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [newSubject, setNewSubject] = useState({ name: '', displayName: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      fetchDiaryTemplate();
    }
  }, [selectedClass]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get('/api/classes');
      setClasses(response.data.map(cls => cls.name));
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchDiaryTemplate = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/teacher-diary/${selectedClass}`);
      setSubjects(response.data.subjects || []);
    } catch (error) {
      if (error.response?.status === 404) {
        setSubjects([]);
      } else {
        console.error('Error fetching diary template:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const addSubject = () => {
    if (!newSubject.name.trim() || !newSubject.displayName.trim()) {
      alert('Please enter both subject name and display name');
      return;
    }

    if (subjects.some(s => s.name === newSubject.name)) {
      alert('Subject already exists');
      return;
    }

    setSubjects([...subjects, { ...newSubject }]);
    setNewSubject({ name: '', displayName: '' });
  };

  const removeSubject = (subjectName) => {
    setSubjects(subjects.filter(s => s.name !== subjectName));
  };

  const saveTemplate = async () => {
    if (!selectedClass) {
      alert('Please select a class');
      return;
    }

    if (subjects.length === 0) {
      alert('Please add at least one subject');
      return;
    }

    try {
      setLoading(true);
      await axios.post('/api/teacher-diary', {
        className: selectedClass,
        subjects: subjects,
        createdBy: 'Admin Level 1'
      });
      alert('Diary template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Error saving template');
    } finally {
      setLoading(false);
    }
  };

  const predefinedSubjects = [
    { name: 'mathematics', displayName: 'Mathematics' },
    { name: 'science', displayName: 'Science' },
    { name: 'english', displayName: 'English' },
    { name: 'hindi', displayName: 'Hindi' },
    { name: 'social_science', displayName: 'Social Science' },
    { name: 'physics', displayName: 'Physics' },
    { name: 'chemistry', displayName: 'Chemistry' },
    { name: 'biology', displayName: 'Biology' },
    { name: 'computer_science', displayName: 'Computer Science' },
    { name: 'physical_education', displayName: 'Physical Education' },
    { name: 'art', displayName: 'Art & Craft' },
    { name: 'music', displayName: 'Music' }
  ];

  const addPredefinedSubject = (subject) => {
    if (subjects.some(s => s.name === subject.name)) {
      alert('Subject already exists');
      return;
    }
    setSubjects([...subjects, subject]);
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', width: '90%', maxWidth: '800px', height: '90%', borderRadius: '15px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }}>
        
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)', color: 'white', padding: '20px', position: 'relative' }}>
          <button 
            onClick={onClose}
            style={{ position: 'absolute', right: '20px', top: '20px', background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', fontSize: '18px' }}
          >
            ✕
          </button>
          
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '10px' }}>📚</div>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Teacher Diary Template Manager</h2>
            <p style={{ margin: '8px 0 0 0', fontSize: '16px', opacity: 0.9 }}>Configure subjects for each class diary</p>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
          
          {/* Class Selection */}
          <div style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '2px solid #2196f3' }}>
            <h4 style={{ margin: '0 0 15px 0', color: '#1565c0', fontSize: '18px' }}>🎓 Select Class</h4>
            <select 
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              style={{ width: '100%', padding: '12px', border: '2px solid #2196f3', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold' }}
            >
              <option value="">Choose a class...</option>
              {classes.map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>

          {selectedClass && (
            <>
              {/* Add Subject Section */}
              <div style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', padding: '20px', borderRadius: '12px', marginBottom: '20px', border: '2px solid #6c757d' }}>
                <h4 style={{ margin: '0 0 15px 0', color: '#495057', fontSize: '18px' }}>➕ Add Custom Subject</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '15px', alignItems: 'end', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#495057' }}>Subject Code:</label>
                    <input 
                      type="text"
                      value={newSubject.name}
                      onChange={(e) => setNewSubject({...newSubject, name: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                      placeholder="e.g., mathematics"
                      style={{ width: '100%', padding: '10px', border: '2px solid #ced4da', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#495057' }}>Display Name:</label>
                    <input 
                      type="text"
                      value={newSubject.displayName}
                      onChange={(e) => setNewSubject({...newSubject, displayName: e.target.value})}
                      placeholder="e.g., Mathematics"
                      style={{ width: '100%', padding: '10px', border: '2px solid #ced4da', borderRadius: '8px', fontSize: '14px' }}
                    />
                  </div>
                  <button 
                    onClick={addSubject}
                    style={{ background: '#28a745', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
                  >
                    ➕ Add
                  </button>
                </div>

                {/* Predefined Subjects */}
                <div>
                  <h5 style={{ margin: '0 0 10px 0', color: '#495057' }}>📋 Quick Add Common Subjects:</h5>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {predefinedSubjects.map(subject => (
                      <button 
                        key={subject.name}
                        onClick={() => addPredefinedSubject(subject)}
                        disabled={subjects.some(s => s.name === subject.name)}
                        style={{ 
                          background: subjects.some(s => s.name === subject.name) ? '#e0e0e0' : '#007bff', 
                          color: subjects.some(s => s.name === subject.name) ? '#666' : 'white', 
                          border: 'none', 
                          padding: '6px 12px', 
                          borderRadius: '6px', 
                          cursor: subjects.some(s => s.name === subject.name) ? 'not-allowed' : 'pointer', 
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        {subject.displayName}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Current Subjects */}
              <div style={{ background: 'white', border: '2px solid #28a745', borderRadius: '12px', overflow: 'hidden', marginBottom: '20px' }}>
                <div style={{ background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)', color: 'white', padding: '15px' }}>
                  <h4 style={{ margin: 0, fontSize: '18px' }}>📚 Subjects for Class {selectedClass} ({subjects.length})</h4>
                </div>
                
                <div style={{ padding: '20px' }}>
                  {subjects.length > 0 ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '15px' }}>
                      {subjects.map((subject, index) => (
                        <div key={subject.name} style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontWeight: 'bold', color: '#495057', marginBottom: '4px' }}>{subject.displayName}</div>
                            <div style={{ fontSize: '12px', color: '#6c757d' }}>Code: {subject.name}</div>
                          </div>
                          <button 
                            onClick={() => removeSubject(subject.name)}
                            style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer', fontSize: '14px' }}
                            title="Remove subject"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', color: '#6c757d', padding: '40px' }}>
                      <div style={{ fontSize: '48px', marginBottom: '15px' }}>📚</div>
                      <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>No subjects added yet</div>
                      <div style={{ fontSize: '14px' }}>Add subjects using the form above</div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ background: '#f8f9fa', padding: '20px', borderTop: '2px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ color: '#6c757d', fontSize: '14px' }}>
            {selectedClass ? `Configuring diary for Class ${selectedClass}` : 'Select a class to start configuring'}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={onClose}
              style={{ background: '#6c757d', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}
            >
              Cancel
            </button>
            {selectedClass && (
              <button 
                onClick={saveTemplate}
                disabled={loading || subjects.length === 0}
                style={{ 
                  background: loading || subjects.length === 0 ? '#e0e0e0' : '#28a745', 
                  color: loading || subjects.length === 0 ? '#666' : 'white', 
                  border: 'none', 
                  padding: '12px 20px', 
                  borderRadius: '8px', 
                  cursor: loading || subjects.length === 0 ? 'not-allowed' : 'pointer', 
                  fontSize: '14px', 
                  fontWeight: 'bold' 
                }}
              >
                {loading ? '💾 Saving...' : '💾 Save Template'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DiaryTemplateManager;